import React from "react";
import { Link } from "react-router-dom";
import BrandMark from "./BrandMark";
import MailingListFooterCTA from "./MailingListFooterCTA";
import { FOOTER_PRODUCTS, FOOTER_EXTRAS } from "../../data/productCatalog";
import { SEO_LOCATIONS } from "../../data/seoLocations";
import { SEO_INDUSTRIES } from "../../data/seoIndustries";

// Footer products column. Sourced from src/data/productCatalog.js (single
// source of truth) plus a handful of footer-only entries (e.g. /marketplace).
const PRODUCT_FOOTER_LINKS = [...FOOTER_PRODUCTS, ...FOOTER_EXTRAS];

const SUPPORT_FOOTER_LINKS = [
  { to: "/support",                   label: "Customer Support",      testid: "footer-support-general" },
  { to: "/support/website",           label: "Website Support",       testid: "footer-support-website" },
  { to: "/support/funnel",            label: "Funnel Support",        testid: "footer-support-funnel" },
  { to: "/support/revisions",         label: "Revision Requests",     testid: "footer-support-revisions" },
  { to: "/support/growth-points",     label: "GrowthPoints Submission", testid: "footer-support-growth-points" },
  { to: "/support/unlimited-design",  label: "Unlimited Design Submission", testid: "footer-support-unlimited-design" },
];

const LEGAL_FOOTER_LINKS = [
  { to: "/about-bow-tie-kreative", label: "About Bow Tie Kreative", testid: "footer-about-btk" },
  { to: "/blog",                  label: "Blog",               testid: "footer-blog", badge: "NEW" },
  { href: "https://login.bowtiekreative.com", label: "Client Portal", testid: "footer-client-portal", badge: "LOGIN", external: true },
  { to: "/onboarding",            label: "Client Onboarding",  testid: "footer-onboarding", badge: "PORTAL" },
  { to: "/docs",                  label: "Documentation",      testid: "footer-docs",       badge: "HELP" },
  { to: "/admin/cms",             label: "CMS Admin",          testid: "footer-cms-admin",  badge: "ADMIN" },
  { to: "/vault/login",           label: "Vault Login",        testid: "footer-vault-login" },
  { to: "/privacy-policy",        label: "Privacy Policy",     testid: "footer-privacy-policy" },
  { to: "/cookie-policy",         label: "Cookie Policy",      testid: "footer-cookie-policy" },
  { to: "/terms-and-conditions",  label: "Terms & Conditions", testid: "footer-terms-conditions" },
  { to: "/refund-policy",         label: "Refund Policy",      testid: "footer-refund-policy" },
];

const SITEMAP_HREF = "/sitemap.xml";

const SOCIAL_LINKS = [
  {
    href: "https://x.com/bowtiekreative",
    label: "Bow Tie Kreative on X",
    testid: "footer-social-x",
    // X / Twitter glyph
    svg: (
      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
        <path d="M18.244 2H21l-6.52 7.45L22 22h-6.79l-4.72-6.18L4.99 22H2.23l6.97-7.96L2 2h6.93l4.27 5.65L18.24 2zm-1.19 18h1.83L7.04 4H5.13l11.92 16z" />
      </svg>
    ),
  },
  {
    href: "https://www.instagram.com/bowtiekreative",
    label: "Bow Tie Kreative on Instagram",
    testid: "footer-social-instagram",
    svg: (
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    href: "https://www.facebook.com/bowtiekreative",
    label: "Bow Tie Kreative on Facebook",
    testid: "footer-social-facebook",
    svg: (
      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
        <path d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.3-1.5 1.6-1.5h1.7V4.6c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.1v2.3H7.6V14h2.7v8h3.2z" />
      </svg>
    ),
  },
  {
    href: "https://www.youtube.com/@bowtiekreative",
    label: "Bow Tie Kreative on YouTube",
    testid: "footer-social-youtube",
    svg: (
      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
        <path d="M21.6 7.2c-.2-.9-.9-1.6-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4c-.9.2-1.6.9-1.8 1.8C2 8.8 2 12 2 12s0 3.2.4 4.8c.2.9.9 1.6 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4c.9-.2 1.6-.9 1.8-1.8.4-1.6.4-4.8.4-4.8s0-3.2-.4-4.8zM10 15V9l5.2 3L10 15z" />
      </svg>
    ),
  },
];

export default function Footer() {
  return (
    <>
      <MailingListFooterCTA />
      <footer data-testid="footer" className="relative pt-16 pb-10 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Top row — products, brand, social */}
        <div className="grid md:grid-cols-12 gap-10 md:gap-12 pb-12 border-b border-white/5">
          <div className="md:col-span-4 space-y-4">
            <Link to="/" data-testid="footer-brand-link" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
              <BrandMark size={36} testid="footer-brand-mark" />
              <span className="font-display text-lg">
                Bow Tie <span className="bk-gold-text">Kreative</span>
              </span>
            </Link>
            <p className="text-sm text-white/55 leading-relaxed max-w-sm">
              Websites, sales funnels, and content systems that turn visitors into customers — led by Ryan Perez.
            </p>
            <a
              href="tel:+18887550507"
              data-testid="footer-phone"
              className="inline-flex items-center gap-2 text-sm text-white/85 hover:text-gold transition-colors mt-2"
              aria-label="Call Bow Tie Kreative at 888-755-0507"
            >
              <svg className="w-4 h-4 text-gold" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M2.3 4.6c0-1.3 1-2.4 2.3-2.4h2c1 0 1.8.7 2 1.6l.6 2.4c.2.8-.1 1.6-.8 2L7 9.1c1 2 2.6 3.6 4.6 4.6l.9-1.4c.4-.7 1.2-1 2-.8l2.4.6c.9.2 1.6 1 1.6 2v2c0 1.3-1.1 2.3-2.4 2.3C8.1 18.4 1.6 11.9 2.3 4.6z"/>
              </svg>
              <span className="font-semibold tracking-wide">(888) 755-0507</span>
            </a>
            <div className="flex items-center gap-3 pt-1">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.testid}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={s.testid}
                  aria-label={s.label}
                  className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold/50 transition-colors"
                >
                  {s.svg}
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-8">
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-8">
              <div className="md:col-span-2">
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-4">
                  Products & Services
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {PRODUCT_FOOTER_LINKS.map((p) => (
                    <Link
                      key={p.testid}
                      to={p.to}
                      data-testid={p.testid}
                      className="text-white/65 hover:text-gold transition-colors inline-flex items-center gap-2"
                    >
                      {p.label}
                      {p.badge && (
                        <span
                          className="text-[9px] uppercase tracking-[0.18em] font-bold text-black px-1.5 py-0.5 rounded-full leading-none"
                          style={{ background: "linear-gradient(135deg, #f3d172 0%, #D4AF37 60%, #a07e1f 100%)" }}
                        >
                          {p.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-4">
                  Support
                </div>
                <div className="flex flex-col gap-2 text-sm">
                  {SUPPORT_FOOTER_LINKS.map((s) => (
                    <Link
                      key={s.testid}
                      to={s.to}
                      data-testid={s.testid}
                      className="text-white/65 hover:text-gold transition-colors"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom row — copyright + legal */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8">
          <div className="text-xs text-white/55 text-center md:text-left">
            © {new Date().getFullYear()} Bow Tie Kreative. All rights reserved.
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/55">
            {LEGAL_FOOTER_LINKS.map((l) => {
              const inner = (
                <>
                  {l.label}
                  {l.badge && (
                    <span
                      className="ml-1.5 text-[8px] uppercase tracking-[0.18em] font-bold text-black px-1.5 py-0.5 rounded-full leading-none align-middle"
                      style={{ background: "linear-gradient(135deg, #f3d172 0%, #D4AF37 60%, #a07e1f 100%)" }}
                    >
                      {l.badge}
                    </span>
                  )}
                </>
              );
              if (l.external) {
                return (
                  <a
                    key={l.testid}
                    href={l.href}
                    data-testid={l.testid}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gold transition-colors inline-flex items-center"
                  >
                    {inner}
                  </a>
                );
              }
              return (
                <Link
                  key={l.testid}
                  to={l.to}
                  data-testid={l.testid}
                  className="hover:text-gold transition-colors inline-flex items-center"
                >
                  {inner}
                </Link>
              );
            })}
            <a
              href={SITEMAP_HREF}
              data-testid="footer-sitemap"
              className="hover:text-gold transition-colors"
            >
              Sitemap (XML)
            </a>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("btk:open-cookie-settings"));
                }
              }}
              data-testid="footer-cookie-settings"
              className="hover:text-gold transition-colors cursor-pointer"
            >
              Cookie Settings
            </button>
          </div>
        </div>

        <p className="mt-10 text-[11px] text-white/30 leading-relaxed text-center max-w-3xl mx-auto">
          DISCLAIMER: This site is not a part of the Facebook website or Facebook Inc.
          Additionally, this site is NOT endorsed by Facebook in any way.
          FACEBOOK is a trademark of FACEBOOK, INC.
        </p>

        {/* SEO directory — programmatic city + industry pages, surfaced as
            dense multi-column lists so we fill the available footer width
            (was a sparse 6-item column with lots of dead space). Pulls live
            from the SEO data files so this never drifts from the index pages.
            Keeps a single "All …" link at the bottom of each block. */}
        <div
          data-testid="footer-seo-directory"
          className="mt-14 pt-10 border-t border-white/10 grid lg:grid-cols-2 gap-10 text-left"
        >
          <div data-testid="footer-seo-service-areas">
            <p className="text-[10px] uppercase tracking-[0.28em] text-white/40 mb-5">
              Service areas
            </p>
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-2 text-[12px] text-white/55">
              {SEO_LOCATIONS.map((l) => (
                <li key={l.slug}>
                  <a
                    href={`/locations/${l.slug}`}
                    data-testid={`footer-loc-${l.slug}`}
                    className="hover:text-gold transition-colors"
                  >
                    {l.city}
                  </a>
                </li>
              ))}
              <li className="col-span-2 sm:col-span-3 mt-2">
                <a href="/locations" className="hover:text-gold font-semibold" data-testid="footer-loc-all">
                  All service areas →
                </a>
              </li>
            </ul>
          </div>
          <div data-testid="footer-seo-industries">
            <p className="text-[10px] uppercase tracking-[0.28em] text-white/40 mb-5">
              Industries
            </p>
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-2 text-[12px] text-white/55">
              {SEO_INDUSTRIES.map((i) => (
                <li key={i.slug}>
                  <a
                    href={`/industries/${i.slug}`}
                    data-testid={`footer-ind-${i.slug}`}
                    className="hover:text-gold transition-colors"
                  >
                    {i.name}
                  </a>
                </li>
              ))}
              <li className="col-span-2 sm:col-span-3 mt-2">
                <a href="/industries" className="hover:text-gold font-semibold" data-testid="footer-ind-all">
                  All industries →
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}
